import type { Writable } from "svelte/store";
import { FS } from "./fs";
import type { OpendriveConfig } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { throwIf } from "./util";

// https://www.opendrive.com/wp-content/uploads/guides/OpenDrive_API_guide.pdf
// The OpenDrive API answers other origins, so the browser calls it directly
// and the password never reaches our server. The calls follow rclone's
// opendrive backend.
//
// A login gives a session id, which is all we keep. OpenDrive wants it in the
// address or the body of every call, so errors must not quote addresses.
//
// OpenDrive knows folders and files by id and has no call that takes a path.
// A path is walked one folder at a time and the folder ids we learn are
// remembered. Names are case-insensitive.

export const opendriveAPI = "https://dev.opendrive.com/api/v1";

const rootID = "0";
// as in rclone
const chunkSize = 10 * 1024 * 1024;
const maxNameLen = 255;

export const prop_opendrive_md5 = "opendrive_md5";
export const prop_opendrive_link = "opendrive_link";

interface OdFolder {
  FolderID?: string;
  Name?: string;
  // seconds since epoch
  DateModified?: number | string;
}

interface OdFile {
  FileId?: string;
  Name?: string;
  Size?: number | string;
  DateModified?: number | string;
  FileHash?: string;
  Link?: string;
}

interface OdList {
  Folders?: OdFolder[];
  Files?: OdFile[];
}

class OpendriveError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

function enc(s: string): string {
  return encodeURIComponent(s);
}

function splitPath(path: string): string[] {
  return path.split("/").filter((s) => s != "");
}

function dirKey(parts: string[]): string {
  return parts.join("/").toLowerCase();
}

function sameName(a: string | undefined, b: string): boolean {
  return (a || "").toLowerCase() == b.toLowerCase();
}

function findFolder(list: OdList, name: string): OdFolder | undefined {
  return (list.Folders || []).find((f) => f.FolderID && sameName(f.Name, name));
}

function findFile(list: OdList, name: string): OdFile | undefined {
  return (list.Files || []).find((f) => f.FileId && sameName(f.Name, name));
}

// Refuses the names OpenDrive does not take as they are.
export function opendriveCheckName(name: string): void {
  throwIf(name == "", "missing name");
  throwIf(name.length > maxNameLen, `a name cannot be longer than ${maxNameLen} characters`);
  let bad = /[<>:"\\|?*\x00-\x1f]/.test(name) || name != name.trim();
  throwIf(bad, 'a name cannot contain < > : " \\ | ? * or start or end with a space');
}

class FileSystemOpendrive extends FS {
  supportsDirRename = true;

  // folder ids by lower-case path, without slashes at the ends
  dirIDs = new Map<string, string>();

  constructor(config: OpendriveConfig) {
    super(config);
  }

  get sid(): string {
    return (this.config as OpendriveConfig).sessionID;
  }

  list(folderID: string): Promise<OdList> {
    return odCall("GET", `/folder/list.json/${enc(this.sid)}/${enc(folderID)}`);
  }

  // Remembers the folders of the folder at parts, all of them: what we knew
  // about folders that are gone or were replaced is forgotten.
  remember(parts: string[], list: OdList) {
    let prefix = parts.length > 0 ? dirKey(parts) + "/" : "";
    let fresh = new Map<string, string>();
    for (let f of list.Folders || []) {
      if (f.FolderID && f.Name) {
        fresh.set(f.Name.toLowerCase(), f.FolderID);
      }
    }
    let old = new Map(this.dirIDs);
    for (let k of old.keys()) {
      if (!k.startsWith(prefix)) {
        continue;
      }
      // the folder of this listing that k is, or is under
      let child = k.substring(prefix.length).split("/")[0];
      if (fresh.get(child) != old.get(prefix + child)) {
        this.dirIDs.delete(k);
      }
    }
    for (let [name, id] of fresh) {
      this.dirIDs.set(prefix + name, id);
    }
  }

  async mkdir(parentID: string, name: string): Promise<string> {
    opendriveCheckName(name);
    let js = await odCall("POST", "/folder.json", {
      session_id: this.sid,
      folder_name: name,
      folder_sub_parent: parentID,
      // private, and no public upload, listing or download
      folder_is_public: 0,
      folder_public_upl: 0,
      folder_public_display: 0,
      folder_public_dnl: 0,
    });
    throwIf(!js.FolderID, "OpenDrive did not create the folder");
    return js.FolderID as string;
  }

  // Finds the id of the folder at parts. With create it makes the folders
  // that are missing.
  async resolveDir(parts: string[], create: boolean): Promise<string> {
    let id = rootID;
    // start at the deepest folder we remember
    let start = 0;
    for (let n = parts.length; n > 0; n--) {
      let known = this.dirIDs.get(dirKey(parts.slice(0, n)));
      if (known) {
        id = known;
        start = n;
        break;
      }
    }
    for (let i = start; i < parts.length; i++) {
      let list = await this.list(id);
      this.remember(parts.slice(0, i), list);
      let found = findFolder(list, parts[i]);
      if (found) {
        id = found.FolderID!;
        continue;
      }
      throwIf(!create, `folder not found: /${parts.slice(0, i + 1).join("/")}`);
      id = await this.mkdir(id, parts[i]);
      this.dirIDs.set(dirKey(parts.slice(0, i + 1)), id);
    }
    return id;
  }

  // Lists the folder that holds the entry at parts.
  async parentOf(parts: string[], create: boolean): Promise<{ id: string; list: OdList }> {
    let dir = parts.slice(0, -1);
    let id = await this.resolveDir(dir, create);
    let list = await this.list(id);
    this.remember(dir, list);
    return { id, list };
  }

  // A folder id we remember may be of a folder that is gone. When OpenDrive
  // refuses a call, forget the ids and try once more from the top.
  async fresh<T>(fn: () => Promise<T>): Promise<T> {
    try {
      return await fn();
    } catch (e) {
      let refused = e instanceof OpendriveError && (e.status == 400 || e.status == 404);
      if (!refused || this.dirIDs.size == 0) {
        throw e;
      }
      this.dirIDs.clear();
      return await fn();
    }
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let parts = splitPath(dirPath);
      let list = await this.fresh(async () => this.list(await this.resolveDir(parts, false)));
      this.remember(parts, list);
      return FsDir.fromWire(dirPath, opendriveListToWire(list));
    });
  }

  async fileID(path: string): Promise<string> {
    let parts = splitPath(path);
    throwIf(parts.length == 0, "missing file name");
    return this.fresh(async () => {
      let parent = await this.parentOf(parts, false);
      let f = findFile(parent.list, parts[parts.length - 1]);
      throwIf(!f, `file not found: ${path}`);
      return f!.FileId!;
    });
  }

  // Not a link to share: it carries the session id. It is used for preview
  // and download in this browser only.
  async getPublicURL(path: string): Promise<string> {
    let id = await this.fileID(path);
    return `${opendriveAPI}/download/file.json/${enc(id)}?session_id=${enc(this.sid)}`;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let parts = splitPath(path);
    throwIf(parts.length == 0 || path.endsWith("/"), "missing file name");
    let name = parts[parts.length - 1];
    opendriveCheckName(name);
    let sid = this.sid;

    // a file with this name is replaced, in place
    let target = await this.fresh(async () => {
      let parent = await this.parentOf(parts, true);
      let f = findFile(parent.list, name);
      if (f) {
        return { id: f.FileId!, isNew: false };
      }
      let js = await odCall("POST", "/upload/create_file.json", {
        session_id: sid,
        folder_id: parent.id,
        file_name: name,
      });
      throwIf(!js.FileId, "OpenDrive did not create the file");
      return { id: js.FileId as string, isNew: true };
    });

    let open = await odCall("POST", "/upload/open_file_upload.json", {
      session_id: sid,
      file_id: target.id,
      file_size: file.size,
    });
    let tempLocation = open.TempLocation as string;
    throwIf(!tempLocation, "OpenDrive did not open the upload");

    let started = Date.now();
    for (let offset = 0; offset < file.size; offset += chunkSize) {
      let chunk = file.slice(offset, offset + chunkSize);
      let form = new FormData();
      form.append("session_id", sid);
      form.append("file_id", target.id);
      form.append("temp_location", tempLocation);
      form.append("chunk_offset", String(offset));
      form.append("chunk_size", String(chunk.size));
      form.append("file_data", chunk, name);
      let js = await odSendChunk(form, (loaded) => {
        httpProgress(offset + Math.min(loaded, chunk.size), file.size, Date.now() - started);
      });
      throwIf(Number(js.TotalWritten) != chunk.size, "OpenDrive stored only a part of the file");
    }

    await odCall("POST", "/upload/close_file_upload.json", {
      session_id: sid,
      file_id: target.id,
      file_size: file.size,
      temp_location: tempLocation,
    });
    if (file instanceof File) {
      // seconds: OpenDrive keeps no more than that
      await odCall("PUT", "/file/filesettings.json", {
        session_id: sid,
        file_id: target.id,
        file_modification_time: String(Math.floor(file.lastModified / 1000)),
      });
    }
    if (target.isNew) {
      // private, as rclone does by default. A replaced file keeps its setting.
      await odCall("POST", "/file/access.json", {
        session_id: sid,
        file_id: target.id,
        file_ispublic: 0,
      });
    }
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let isDir = path.endsWith("/");
    let from = splitPath(path);
    let to = splitPath(newPath);
    throwIf(from.length == 0 || to.length == 0, "cannot rename the root folder");
    let fromName = from[from.length - 1];
    let toName = to[to.length - 1];
    opendriveCheckName(toName);
    throwIf(isDir && (dirKey(to) + "/").startsWith(dirKey(from) + "/"), "cannot move a folder into itself");
    let sameParent = dirKey(from.slice(0, -1)) == dirKey(to.slice(0, -1));
    let sid = this.sid;

    try {
      await this.fresh(async () => {
        let src = await this.parentOf(from, false);
        let dst = sameParent ? src : await this.parentOf(to, false);
        let id: string | undefined;
        let takenID: string | undefined;
        if (isDir) {
          id = findFolder(src.list, fromName)?.FolderID;
          takenID = findFolder(dst.list, toName)?.FolderID;
        } else {
          id = findFile(src.list, fromName)?.FileId;
          takenID = findFile(dst.list, toName)?.FileId;
        }
        throwIf(!id, `not found: ${path}`);
        // a rename that only changes the case finds itself
        throwIf(!!takenID && takenID != id, `${toName} already exists`);

        if (isDir && sameParent) {
          await odCall("POST", "/folder/rename.json", { session_id: sid, folder_id: id, folder_name: toName });
        } else if (isDir) {
          await odCall("POST", "/folder/move_copy.json", {
            session_id: sid,
            folder_id: id,
            dst_folder_id: dst.id,
            move: "true",
            new_folder_name: toName,
          });
        } else if (sameParent) {
          await odCall("POST", "/file/rename.json", { session_id: sid, file_id: id, new_file_name: toName });
        } else {
          await odCall("POST", "/file/move_copy.json", {
            session_id: sid,
            src_file_id: id,
            dst_folder_id: dst.id,
            move: "true",
            overwrite_if_exists: "false",
            new_file_name: toName,
          });
        }
      });
    } finally {
      if (isDir) {
        // ids stay but the paths we remember them by changed
        this.dirIDs.clear();
      }
    }
  }

  async createDirectory(dirPath: string): Promise<void> {
    let parts = splitPath(dirPath);
    throwIf(parts.length == 0, "missing directory name");
    // so that its parent is asked, not our memory
    this.dirIDs.delete(dirKey(parts));
    await this.fresh(() => this.resolveDir(parts, true));
  }

  // A folder goes with everything in it.
  async deleteFile(path: string): Promise<void> {
    let parts = splitPath(path);
    if (!path.endsWith("/")) {
      let id = await this.fileID(path);
      await odCall("DELETE", `/file.json/${enc(this.sid)}/${enc(id)}`);
      return;
    }
    throwIf(parts.length == 0, "cannot delete the root folder");
    // not from memory: what we remove must be the folder at this path now
    this.dirIDs.clear();
    let parent = await this.parentOf(parts, false);
    let f = findFolder(parent.list, parts[parts.length - 1]);
    throwIf(!f, `folder not found: ${path}`);
    try {
      await odCall("POST", "/folder/remove.json", { session_id: this.sid, folder_id: f!.FolderID });
    } finally {
      this.dirIDs.clear();
    }
  }
}

function seconds(v: number | string | undefined): number {
  let n = Number(v || 0);
  return Number.isFinite(n) && n > 0 ? n * 1000 : 0;
}

export function opendriveListToWire(list: OdList): any[][] {
  let wires: any[][] = [];
  for (let f of list.Folders || []) {
    if (!f.Name || !f.FolderID) {
      continue;
    }
    wires.push([f.FolderID, f.Name, -1, seconds(f.DateModified), []]);
  }
  for (let f of list.Files || []) {
    if (!f.Name || !f.FileId) {
      continue;
    }
    let size = Number(f.Size || 0);
    let meta: any[] = [f.FileId, f.Name, Number.isFinite(size) ? size : 0, seconds(f.DateModified), null];
    if (f.FileHash) {
      meta.push(prop_opendrive_md5, f.FileHash);
    }
    if (f.Link) {
      meta.push(prop_opendrive_link, f.Link);
    }
    wires.push(meta);
  }
  return wires;
}

/**
 * Log in with a username and password. Returns the session id.
 */
export async function opendriveLogin(username: string, password: string): Promise<string> {
  let js = await odCall("POST", "/session/login.json", { username: username, passwd: password });
  throwIf(!js.SessionID, "OpenDrive did not return a session");
  return js.SessionID as string;
}

function parseJSON(text: string): any {
  try {
    let js = JSON.parse(text);
    return js && typeof js == "object" ? js : {};
  } catch {
    return {};
  }
}

function odError(status: number, text: string, isLogin: boolean): OpendriveError {
  if (status == 401 && !isLogin) {
    return new OpendriveError(status, "OpenDrive session ended: connect again");
  }
  let msg = parseJSON(text).error?.message;
  return new OpendriveError(status, msg ? `OpenDrive: ${msg}` : `OpenDrive ${status}`);
}

async function odCall(method: string, path: string, body?: object): Promise<any> {
  let init: RequestInit = { method };
  if (body) {
    init.headers = { "Content-Type": "application/json" };
    init.body = JSON.stringify(body);
  }
  let rsp: Response;
  try {
    rsp = await fetch(opendriveAPI + path, init);
  } catch {
    // the default message would quote the url, which carries the session id
    throw new Error("could not connect to OpenDrive");
  }
  let text = await rsp.text();
  if (!rsp.ok) {
    throw odError(rsp.status, text, path.startsWith("/session/"));
  }
  return parseJSON(text);
}

// Posts a part of a file. XMLHttpRequest because fetch does not report how
// much was sent.
function odSendChunk(form: FormData, onProgress: (loaded: number) => void): Promise<any> {
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open("POST", opendriveAPI + "/upload/upload_file_chunk.json");
    xhr.responseType = "text";
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parseJSON(xhr.responseText));
        return;
      }
      reject(odError(xhr.status, xhr.responseText, false));
    };
    xhr.onerror = () => {
      reject(new Error("upload to OpenDrive failed"));
    };
    xhr.upload.onprogress = (ev: ProgressEvent) => {
      onProgress(ev.loaded);
    };
    xhr.send(form);
  });
}

export function makeFileSystemOpendrive(c: OpendriveConfig): FileSystemOpendrive {
  return new FileSystemOpendrive(c);
}
